<script setup lang="ts">
import RemedyTooltip from '@/components/panels/RightPanel/remediesPanel/RemedyTooltip.vue'
import ReferencesTooltip from '@/components/references/ReferencesTooltip.vue'
import UIDropdownItems from '@/components/ui/UIDropdownItems.vue'
import UITitleDescriptionTooltip from '@/components/ui/UITitleDescriptionTooltip.vue'
import { useTutorialStore } from '@/store/tutorial'
import { ToolTipEventKey } from '@/utils/eventBusKeys/tooltipKey'
import { IToolTipSpecifications, IToolTipState } from '@lifk/types'
import { useEventBus } from '@vueuse/core'
import { computed, nextTick, ref, Ref, watch } from 'vue'
import { useRoute } from 'vue-router'

/**
 * See UIIconButton for an example of a control modified to accept a tooltip,
 * and MapControls for an example of several tooltips in action.
 *
 * When the delayTag prop is specified, the tooltip will wait half a second
 * before hiding. This allows the user time to move their cursor from the
 * tooltip target to the tooltip itself, which will prevent it from hiding.
 */

const element: Ref<HTMLElement | null> = ref(null)
const tooltipHovered = ref(false)
const clampOffset = ref(0)
const clampOffsetY = ref(0)
let hideTimeout: ReturnType<typeof setTimeout> | undefined
let clickListenerTimeout: ReturnType<typeof setTimeout> | undefined
const toolTipEventBus = useEventBus(ToolTipEventKey)
const route = useRoute()
const tutorialStore = useTutorialStore()

const defaultSpecs: IToolTipSpecifications = {
  text: '',
  remedy: undefined,
  reference: undefined,
  titleDescription: undefined,
  dropdownItems: undefined,
  direction: 'top',
  alignment: 'center',
  maxWidthPx: undefined,
  offset: 10,
  fullWidthTarget: false,
  variant: 'dark',
  toggleOnClick: false,
  delayTag: '',
  targetRect: new DOMRect(),
  textSize: 'auto'
}
const specs: Ref<IToolTipSpecifications> = ref(defaultSpecs)

watch(
  () => route.fullPath,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      hide()
    }
  }
)

watch(
  () => tutorialStore.isActive,
  (active) => {
    if (active) hide()
  }
)

const state: Ref<IToolTipState> = ref({
  visible: false,
  proxyDirection: specs.value.direction, // we use proxies in case we need to make programmatic adjustments
  proxyAlignment: specs.value.alignment
})

const globalEscape = (e: KeyboardEvent) => {
  if (e.code === 'Escape') {
    e.preventDefault()
    hide()
  }
}

function eventIsInsideTooltipTargetEl(clickedX: number, clickedY: number) {
  const targetRectLeft = specs.value.targetRect.x
  const targetRectRight =
    specs.value.targetRect.x + specs.value.targetRect.width
  const targetRectTop = specs.value.targetRect.y
  const targetRectBottom =
    specs.value.targetRect.y + specs.value.targetRect.height

  return (
    clickedX >= targetRectLeft &&
    clickedX <= targetRectRight &&
    clickedY >= targetRectTop &&
    clickedY <= targetRectBottom
  )
}

const eventTargetContainsThis = (evTarget: EventTarget | null): boolean => {
  const targetRect = (evTarget as HTMLElement).getBoundingClientRect()
  const x = targetRect.x
  const y = targetRect.y

  return !!(
    evTarget &&
    (evTarget === element.value ||
      element.value?.contains(evTarget as Node) ||
      eventIsInsideTooltipTargetEl(x, y))
  )
}

const onDocumentClick = (ev: MouseEvent | TouchEvent) => {
  const clickedThis = eventTargetContainsThis(ev.target)
  if (!clickedThis) {
    hide()
  }
}

// events emitted by tooltip target (UIToolTip.vue)
toolTipEventBus.on((event) => {
  if (
    tutorialStore.isActive &&
    (event.type === 'show' || event.type === 'toggle')
  )
    return
  switch (event.type) {
    case 'show':
      // Don't let a hover tooltip override an active popover
      if (
        state.value.visible &&
        specs.value.toggleOnClick &&
        !event.specs?.toggleOnClick
      )
        break
      show(event.specs)
      break
    case 'toggle': {
      // Toggle based on whether THIS specific tooltip is showing, not whether any tooltip is showing
      const isSameTarget =
        state.value.visible &&
        event.specs?.targetRect &&
        specs.value.targetRect.x === event.specs.targetRect.x &&
        specs.value.targetRect.y === event.specs.targetRect.y
      if (isSameTarget) {
        hide()
      } else {
        show(event.specs)
      }
      break
    }
    case 'hide': {
      // If specs are provided, only hide if the event targets the currently-showing tooltip.
      // Spec-less hide (e.g. from SketchLayersControl) is always treated as a global close.
      const hasTargetRect = !!event.specs?.targetRect
      const specMatches =
        hasTargetRect &&
        specs.value.targetRect.x === event.specs!.targetRect.x &&
        specs.value.targetRect.y === event.specs!.targetRect.y
      if (!hasTargetRect || specMatches) hide()
      break
    }
    case 'pointerLeaveTarget':
      setHideTimeout()
      break
  }
})

const show = (tooltipSpecs?: IToolTipSpecifications) => {
  clearTimeout(hideTimeout)
  if (state.value.visible) {
    // if previously visible, this indicates a different tooltip was showing
    //   emit afterHide for the previous tooltip
    toolTipEventBus.emit({ type: 'afterHide', specs: specs.value })
  }

  if (tooltipSpecs) {
    specs.value = tooltipSpecs
  }
  state.value.visible = true
  state.value.proxyAlignment = specs.value.alignment
  state.value.proxyDirection = specs.value.direction
  clampOffset.value = 0
  clampOffsetY.value = 0
  toolTipEventBus.emit({ type: 'afterShow', specs: specs.value })
  nextTick(checkPosition)
  document.addEventListener('keydown', globalEscape, false)
  document.addEventListener('dragstart', hide, false)
  // Defer click listener to avoid the triggering click from immediately closing the tooltip
  clickListenerTimeout = setTimeout(() => {
    document.addEventListener('touchend', onDocumentClick, false)
    document.addEventListener('click', onDocumentClick)
  }, 0)
}

const hide = () => {
  clearTimeout(clickListenerTimeout)
  state.value.visible = false
  toolTipEventBus.emit({ type: 'afterHide', specs: specs.value })
  document.removeEventListener('keydown', globalEscape)
  document.removeEventListener('touchend', onDocumentClick)
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('dragstart', hide, false)
}

const shouldHideOnPtrLeave = computed(
  () => !tooltipHovered.value && !specs.value.toggleOnClick
)

const hasInteractiveContent = computed(
  () => !!specs.value.reference || !!specs.value.dropdownItems
)

const onFocusOut = (evt: FocusEvent) => {
  if (
    state.value.visible &&
    !element.value?.contains(evt.relatedTarget as Node)
  )
    hide()
}

const onPointerLeave = (evt: PointerEvent) => {
  if (evt.pointerType === 'mouse' && shouldHideOnPtrLeave.value) {
    hideIfNotHovered()
  }
}

const setHideTimeout = () => {
  clearTimeout(hideTimeout)
  hideTimeout = setTimeout(hideIfNotHovered, specs.value.delayTag ? 500 : 0)
}

const hideIfNotHovered = () => {
  if (shouldHideOnPtrLeave.value) {
    hide()
  }
}

const alignmentType = computed(() => {
  return state.value.proxyDirection == 'top' ||
    state.value.proxyDirection == 'bottom'
    ? 'vertical'
    : 'horizontal'
})

// dynamically alter direction and alignment if the tooltip
// exceeds the bounds of the window
const checkPosition = async () => {
  if (!element.value) return

  const bounding: DOMRect = element.value.getBoundingClientRect()
  // Use visualViewport on mobile for the actual visible area
  const vw = window.visualViewport?.width ?? window.innerWidth
  const vh = window.visualViewport?.height ?? window.innerHeight

  const checkVerticalAlignment = () => {
    // only flip alignment-direction if the tooltip target is nearer the offending edge
    // this prevents us from flipping into another bad position
    if (bounding.right > vw && specs.value.targetRect.right > vw * 0.75) {
      state.value.proxyAlignment = 'left'
    } else if (bounding.left < 0 && specs.value.targetRect.left < vw * 0.75) {
      state.value.proxyAlignment = 'right'
    } else if (bounding.right > vw || bounding.left < 0) {
      state.value.proxyAlignment = 'center'
    }
    if (bounding.bottom > vh && specs.value.targetRect.bottom > vh * 0.5) {
      state.value.proxyDirection = 'top'
    } else if (bounding.top < 0 && specs.value.targetRect.top < vh * 0.5) {
      state.value.proxyDirection = 'bottom'
    }
  }

  const checkHorizontalAlignment = () => {
    if (bounding.right > vw) {
      state.value.proxyDirection = 'left'
    } else if (bounding.left < 0) {
      state.value.proxyDirection = 'right'
    }
    if (bounding.bottom > vh) {
      state.value.proxyAlignment = 'top'
    } else if (bounding.top < 0) {
      state.value.proxyAlignment = 'bottom'
    }
  }

  if (alignmentType.value == 'vertical') {
    checkVerticalAlignment()
  } else if (alignmentType.value == 'horizontal') {
    checkHorizontalAlignment()
  }

  // Second pass: after alignment adjustments re-render, clamp any remaining overflow
  // with a pixel nudge so the tooltip always fits on screen (for mobile).
  await nextTick()
  if (!element.value) return
  const clamped = element.value.getBoundingClientRect()
  if (clamped.right > vw) {
    clampOffset.value = vw - clamped.right
  } else if (clamped.left < 0) {
    clampOffset.value = -clamped.left
  }
  if (clamped.bottom > vh) {
    clampOffsetY.value = vh - clamped.bottom
  } else if (clamped.top < 0) {
    clampOffsetY.value = -clamped.top
  }
}

// this helps us push the tooltip away from the
// target so the caret doesn't overlap it
const caretHeight = 5.5

// magic number that gets the caret pointing at the center of the target
const caretOffset = caretHeight * 3

const positionStyles = computed(() => {
  const styles: any = {}
  let hTransform = '0px'
  let vTransform = '0px'

  const offset = specs.value.offset

  // these help us correct the tooltip position since
  // by default the tooltip is centered on the target
  const halfTargetWidth = specs.value.targetRect.width / 2
  const halfTargetHeight = specs.value.targetRect.height / 2

  // these help us center the caret on the target
  const hOffset = halfTargetWidth - caretOffset

  // broadly position the tooltip wrt the target along the primary axis
  switch (state.value.proxyDirection) {
    case 'top':
      vTransform = `calc(-100% - ${halfTargetHeight + caretHeight + offset}px)`
      break
    case 'bottom':
      vTransform = `${halfTargetHeight + caretHeight + offset}px`
      break
    case 'left':
      hTransform = `calc(-50% - ${halfTargetWidth + caretHeight + offset}px)`
      break
    case 'right':
      hTransform = `calc(50% + ${halfTargetWidth + caretHeight + offset}px)`
      break
  }

  // slide the tooltip to align with the target on the secondary axis
  switch (state.value.proxyAlignment) {
    case 'left':
      hTransform = `calc(-50% + ${halfTargetWidth - hOffset}px)`
      break
    case 'center':
      if (alignmentType.value == 'horizontal') {
        vTransform = '-50%'
      }
      break
    case 'right':
      hTransform = `calc(50% - ${halfTargetWidth - hOffset}px)`
      break
    case 'top':
      vTransform = `calc(-100% + ${halfTargetHeight - hOffset}px)`
      break
    case 'bottom':
      vTransform = `${-halfTargetHeight + hOffset}px`
      break
  }

  const hOffsetPx = (specs.value.horizontalOffset ?? 0) + clampOffset.value
  styles.transform = `translate(calc(${hTransform} + ${hOffsetPx}px), calc(${vTransform} + ${clampOffsetY.value}px))`
  // Counter-shift the caret so it keeps pointing at the original target despite the clamp.
  styles['--caret-clamp'] = `${-clampOffset.value}px`
  styles['--caret-clamp-y'] = `${-clampOffsetY.value}px`
  return styles
})

const tooltipClasses = computed(() => {
  let classes = ''

  // background + border
  if (specs.value.variant === 'light') {
    classes = 'bg-default border-[3px] border-solid border-inverse'
  } else if (specs.value.variant === 'dropdown') {
    classes = 'bg-elevated border border-solid border-slate-tint-3'
  } else {
    classes = 'bg-inverse'
  }

  // text color
  if (specs.value.variant === 'light' || specs.value.variant === 'dropdown') {
    classes += ' text-default'
  } else {
    classes += ' text-inverse'
  }

  // padding
  if (specs.value.variant === 'dropdown') {
    classes += ''
  } else {
    if (
      state.value.proxyDirection === 'left' ||
      state.value.proxyDirection === 'right'
    ) {
      // min height to accommodate the caret height
      classes += ' min-h-7 content-center'
    }
    if (specs.value.variant === 'info') {
      classes += ' px-padding-panel-container-horz py-padding-default'
    } else {
      classes += ' px-padding-default py-0.5'
    }
  }

  return classes
})

const tooltipWidthStyle = computed(() => {
  // dropdowns should match parent width, or use explicit minWidth if provided
  if (specs.value.variant === 'dropdown') {
    const explicitMinWidth = specs.value.dropdownItems?.minWidth
    return {
      minWidth: explicitMinWidth || `${specs.value.targetRect.width}px`
    }
  }

  // max width
  return {
    width: specs.value.maxWidthPx ? 'max-content' : '',
    maxWidth: `${specs.value.maxWidthPx}px`,
    whiteSpace: specs.value.maxWidthPx ? 'normal' : 'nowrap'
  }
})
</script>

<template>
  <div
    id="floatingToolTip"
    class="fixed z-[90] flex justify-center pointer-events-none"
    :class="{ 'w-full': !!specs.fullWidthTarget }"
    :style="{
      top: specs.targetRect.top + specs.targetRect.height * 0.5 + 'px',
      left: specs.targetRect.left + specs.targetRect.width * 0.5 + 'px'
    }"
    @pointerleave="onPointerLeave"
  >
    <span
      ref="element"
      class="tooltip-parent min-w-5 absolute z-[70]"
      :class="`${specs.variant} direction-${state.proxyDirection} alignment-${state.proxyAlignment} ${
        state.visible
          ? 'pointer-events-auto opacity-100 transition-[opacity] duration-150 ease-in'
          : 'pointer-events-none opacity-0'
      }`"
      :style="positionStyles"
      :inert="!state.visible"
      @focusout="onFocusOut"
      @pointerenter="
        (evt) => {
          if (evt.pointerType === 'mouse') tooltipHovered = true
        }
      "
      @pointerleave="
        (evt) => {
          if (evt.pointerType === 'mouse') tooltipHovered = false
        }
      "
    >
      <span
        class="tooltip rounded-small shadow inline-block"
        :class="tooltipClasses"
        :style="tooltipWidthStyle"
        role="tooltip"
        :aria-hidden="
          hasInteractiveContent && state.visible ? undefined : 'true'
        "
      >
        <RemedyTooltip v-if="specs.remedy" :remedy="specs.remedy" />
        <ReferencesTooltip
          v-else-if="specs.reference"
          :item="specs.reference"
        />
        <UITitleDescriptionTooltip
          v-else-if="specs.titleDescription"
          :title-description="specs.titleDescription"
        />
        <UIDropdownItems
          v-else-if="specs.dropdownItems && state.visible"
          :dropdown-id="specs.dropdownItems.dropdownId"
          :dropdown-title="specs.dropdownItems.dropdownTitle"
          :options="specs.dropdownItems.items"
          :active-style="specs.dropdownActiveStyle"
          :show-direction-arrow="specs.dropdownItems.showDirectionArrow ?? true"
          :show-item-borders="specs.dropdownItems.showItemBorders ?? false"
        />
        <span
          v-else
          :class="{
            'font-normal text-body-sm':
              specs.text &&
              ((specs.text.length > 30 && specs.textSize === 'auto') ||
                specs.textSize === 'small'),
            'font-medium text-body':
              specs.text &&
              ((specs.text.length <= 30 && specs.textSize === 'auto') ||
                specs.textSize === 'default')
          }"
        >
          <!-- eslint-disable vue/no-v-html -->
          <p v-html="specs.text"></p>
        </span>
      </span>
    </span>
  </div>
</template>

<style scoped>
.tooltip-parent {
  will-change: opacity;
}

/* general caret */
.tooltip-parent.direction-bottom::before,
.tooltip-parent.direction-top::after,
.tooltip-parent.direction-left::before,
.tooltip-parent.direction-right::after {
  content: '';
  transform: rotate(45deg);
  @apply absolute block w-[11px] h-[11px];
}
.tooltip-parent.dark.direction-bottom::before,
.tooltip-parent.dark.direction-top::after,
.tooltip-parent.dark.direction-left::before,
.tooltip-parent.dark.direction-right::after,
.tooltip-parent.info.direction-bottom::before,
.tooltip-parent.info.direction-top::after,
.tooltip-parent.info.direction-left::before,
.tooltip-parent.info.direction-right::after {
  @apply z-[-1] bg-inverse;
}
.tooltip-parent.light.direction-bottom::before,
.tooltip-parent.light.direction-top::after,
.tooltip-parent.light.direction-left::before,
.tooltip-parent.light.direction-right::after {
  @apply bg-elevated;
}

/* top caret */
.tooltip-parent.direction-bottom::before {
  top: -5px;
  @apply rounded-tl-xs;
}
/* bottom caret */
.tooltip-parent.direction-top::after {
  top: calc(100% - 6px);
  @apply rounded-br-xs;
}
/* right caret */
.tooltip-parent.direction-left::before {
  left: calc(100% - 6px);
  @apply rounded-tr-xs;
}
/* left caret */
.tooltip-parent.direction-right::after {
  left: -6px;
  @apply rounded-bl-xs;
}

/* center vertical caret */
.tooltip-parent.direction-top.tooltip-parent.alignment-center::before,
.tooltip-parent.direction-top.tooltip-parent.alignment-center::after,
.tooltip-parent.direction-bottom.tooltip-parent.alignment-center::before,
.tooltip-parent.direction-bottom.tooltip-parent.alignment-center::after {
  left: calc(50% - 6px + var(--caret-clamp, 0px));
}

/* center horizontal caret */
.tooltip-parent.direction-left.tooltip-parent.alignment-center::before,
.tooltip-parent.direction-left.tooltip-parent.alignment-center::after,
.tooltip-parent.direction-right.tooltip-parent.alignment-center::before,
.tooltip-parent.direction-right.tooltip-parent.alignment-center::after {
  top: calc(50% - 6px + var(--caret-clamp-y, 0px));
}

/*
  these position the caret wrt the tooltip
  the caret is 11px wide and 11px tall
  and inset another 11px from the edge of the tooltip
*/
.tooltip-parent.alignment-left::before,
.tooltip-parent.alignment-left::after {
  left: calc(100% - 22px + var(--caret-clamp, 0px));
}
.tooltip-parent.alignment-top::before,
.tooltip-parent.alignment-top::after {
  top: calc(100% - 22px + var(--caret-clamp-y, 0px));
}
.tooltip-parent.alignment-right::before,
.tooltip-parent.alignment-right::after {
  left: calc(11px + var(--caret-clamp, 0px));
}
.tooltip-parent.alignment-bottom::before,
.tooltip-parent.alignment-bottom::after {
  top: calc(11px + var(--caret-clamp-y, 0px));
}
</style>
